/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { SvgIcon } from '@mui/material';
import Box from '@mui/material/Box';
import { makeStyles } from '@mui/styles';
import React from 'react';
import styled from 'styled-components';
import { getSegmentColorDefinitions } from '../../../../utils/MptFlightPathSegmentingUtils';

interface PerformanceOutlookExportLegendProps {
  flightLegId: number;
}

export const useStyles = makeStyles({
  container: {
    width: '100%',
    lineHeight: '21px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    color: 'white',
    [`@media print`]: {
      width: '100%',
      marginLeft: '0px',
      color: 'black',
    },
  },
});

const PerformanceOutlookExportLegend = ({ flightLegId }: PerformanceOutlookExportLegendProps) => {
  const classes = useStyles();
  return (
    <Box key={flightLegId}>
      <Box className={classes.container}>
        <LegendItem>
          <StyledPOExportLegendIcon color={getSegmentColorDefinitions().mptNominalServiceFlightPathColor} />
          Normal Service
        </LegendItem>
        <LegendItem>
          <StyledPOExportLegendIcon color={getSegmentColorDefinitions().mptMarginalServiceColor} />
          Marginal Service
        </LegendItem>
        <LegendItem>
          <StyledPOExportLegendIcon color={getSegmentColorDefinitions().mptHnaAllAltitudeFlightPathColor} />
          HNA (All Airspace)
        </LegendItem>
        <LegendItem>
          <StyledPOExportLegendIcon color={getSegmentColorDefinitions().mptHnaAltitudeRestrictedFlightPathColor} />
          HNA (Altitude Restricted)
        </LegendItem>
        <LegendItem>
          <StyledPOExportLegendIcon color={getSegmentColorDefinitions().mptNoCoverageFlightPathColor} />
          No Coverage
        </LegendItem>
      </Box>
      <Box className={classes.container}>
        <LegendItem>
          {takeoffSvg}
          Takeoff
        </LegendItem>
        <LegendItem>
          {handoffIcon}
          Handoff
        </LegendItem>
        <LegendItem>
          {serviceAlertIcon}
          Service Alert
        </LegendItem>
        <LegendItem>
          {landingIcon}
          Landing
        </LegendItem>
      </Box>
    </Box>
  );
};

export default PerformanceOutlookExportLegend;

const LegendItem = ({ children }: { children: string | React.ReactNode }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '12px',
        marginRight: '6px',
      }}
    >
      {children}
    </Box>
  );
};

const StyledPOExportLegendIcon = styled.div`
  height: 10px;
  width: 16px;
  margin-right: 2px;
  background-color: ${(props: { color: string }) => props.color};
`;

const takeoffSvg = (
  <SvgIcon>
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 32 32">
      <defs>
        <path id="a" d="M0 0h24v24H0V0z" />
      </defs>

      <circle fill="white" cx="16" cy="16" r="16"></circle>
      <g transform="translate(4, 4)">
        <path
          fill="#141D24"
          d="M2.5 19h19v2h-19zm19.57-9.36c-.21-.8-1.04-1.28-1.84-1.06L14.92 10l-6.9-6.43-1.93.51 4.14 7.17-4.97 1.33-1.97-1.54-1.45.39 1.82 3.16.77 1.33 1.6-.43 5.31-1.42 4.35-1.16L21 11.49c.81-.23 1.28-1.05 1.07-1.85z"
          clip-path="url(#b)"
        />
        <path clip-path="url(#b)" fill="none" d="M0 0h24v24H0V0z" />
      </g>
    </svg>
  </SvgIcon>
);
const handoffIcon = (
  <SvgIcon>
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
      <path fill="none" d="M0 0h24v24H0V0z" />
      <circle cx="12" cy="12" r="9" fill="#141D24" />
      <path
        fill="#FFAF07"
        d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12s4.48 10 10 10 10-4.48 10-10zm-7-5.5l3.5 3.5-3.5 3.5V11h-4V9h4V6.5zm-6 11L5.5 14 9 10.5V13h4v2H9v2.5z"
      />
    </svg>
  </SvgIcon>
);

const serviceAlertIcon = (
  <SvgIcon>
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
      <path d="M0 0h24v24H0z" fill="none" />
      <rect fill="#141D24" x="10" y="5" height="13" width="4"></rect>
      <path
        fill="#f0f0f0"
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
      />
    </svg>
  </SvgIcon>
);
const landingIcon = (
  <SvgIcon>
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 32 32">
      <defs>
        <path id="a" d="M0 0h24v24H0V0z" />
      </defs>
      <circle fill="white" cx="16" cy="16" r="16"></circle>
      <g transform="translate(4, 4)">
        <path
          fill="#141D24"
          d="M2.5 19h19v2h-19zm7.18-5.73l4.35 1.16 5.31 1.42c.8.21 1.62-.26 1.84-1.06.21-.8-.26-1.62-1.06-1.84l-5.31-1.42-2.76-9.02L10.12 2v8.28L5.15 8.95l-.93-2.32-1.45-.39v5.17l1.6.43 5.31 1.43z"
          clip-path="url(#b)"
        />
        <path clip-path="url(#b)" fill="none" d="M0 0h24v24H0V0z" />
      </g>
    </svg>
  </SvgIcon>
);
